import { PlayerName as DisplayedName } from './PlayerName';
import { getPlayerKMultiplier, formatBoost, EngineSettings } from '../../engine';
import { useLocale } from '../../i18n';

export interface PlayerSingleMatchRowProps {
  /** The single match (flat team1/team2 + sets + result) to render. */
  match: any;
  /** The profile player whose perspective this row is rendered from. */
  playerId: any;
  /** Player list used to resolve names for the slots. */
  allPlayers: any[];
}

/**
 * One single-match row in a player profile — date/time, both teams, set-win score
 * and a win/loss tag from the player's perspective. Same styling as PlayerMatchRow.
 * Extracted verbatim from the original artifact (legacy/App.jsx); markup/logic unchanged.
 *
 * Engine swaps (live Padel24Engine.elo.* → named engine imports):
 * getPlayerKMultiplier, formatBoost, EngineSettings.NEWBIE_MATCH_THRESHOLD.
 */
export function PlayerSingleMatchRow({
  match: m,
  playerId,
  allPlayers,
}: PlayerSingleMatchRowProps) {
  const { t, formatDate } = useLocale();
  const inT1 = m.team1.p1Id === playerId || m.team1.p2Id === playerId;
  const t1Won = m.result?.winner === 'team1';
  const t2Won = m.result?.winner === 'team2';

  const playerWon = (inT1 && t1Won) || (!inT1 && t2Won);
  const playerLost = (inT1 && t2Won) || (!inT1 && t1Won);
  const rowBg = playerWon ? 'bg-lime-bg/30' : playerLost ? 'bg-rose-soft/40' : '';

  // NOTE (extraction deviation): the monolith declared an unused
  //   `const eloChange = inT1 ? m.result?.t1Change : m.result?.t2Change;`
  // here. It is dead code (never referenced) and trips no-unused-vars under
  // --max-warnings=0, so it is dropped. No behavioural change.

  // Base change (without new-player boost) — shown on the team edge for consistency
  // with UniversalMatchRow. The actual change (with boost) is shown behind the
  // player's 2x pill where relevant.
  const t1NewbieMul = m.result?.t1NewbieMul ?? 1.0;
  const t2NewbieMul = m.result?.t2NewbieMul ?? 1.0;
  const t1Change = m.result?.t1Change ?? 0;
  const t2Change = m.result?.t2Change ?? 0;
  const t1BaseChange = t1NewbieMul > 0 ? Math.round(t1Change / t1NewbieMul) : t1Change;
  const t2BaseChange = t2NewbieMul > 0 ? Math.round(t2Change / t2NewbieMul) : t2Change;
  const teamBaseChange = inT1 ? t1BaseChange : t2BaseChange;

  const date = new Date(m.playedAt);
  const dateLabel = formatDate(date, { day: '2-digit', month: 'short' });
  const timeLabel = formatDate(date, { hour: '2-digit', minute: '2-digit' });

  const t1SetWins = m.sets.filter((s) => s.games1 > s.games2).length;
  const t2SetWins = m.sets.filter((s) => s.games2 > s.games1).length;

  const playerByIdFn = (id) => allPlayers.find((p) => p.id === id);

  const PlayerName = ({ playerIdRef, isWinner, isT1Side }) => {
    const p = playerByIdFn(playerIdRef);
    if (!p) return <span className="text-xs text-ink-3 italic">?</span>;
    const isMe = playerIdRef === playerId;

    // Check whether the player is currently a new player (boosted)
    const newbieMul = getPlayerKMultiplier(p);
    const isBoosted = newbieMul > 1;
    const teamChange = isT1Side ? t1Change : t2Change;

    return (
      <span
        className={`text-xs leading-tight flex items-center gap-1.5 ${isMe ? 'font-bold text-ink' : 'text-ink-2'} ${isWinner ? 'font-semibold' : ''}`}
      >
        <span className="truncate">
          <DisplayedName player={p} />
        </span>
        {isBoosted && (
          <span
            className="font-mono text-[9px] font-bold px-1 rounded flex-shrink-0 inline-flex items-center gap-1"
            style={{ background: '#fef3c7', color: '#92400e' }}
            title={t('players.common.boostTitle', {
              boost: formatBoost(newbieMul),
              count: EngineSettings.NEWBIE_MATCH_THRESHOLD,
            })}
          >
            <span>{formatBoost(newbieMul)}</span>
            {teamChange != null && (
              <span
                style={{
                  color: teamChange > 0 ? '#3f6212' : teamChange < 0 ? '#9f1239' : '#92400e',
                }}
              >
                {teamChange > 0 ? '+' : ''}
                {teamChange}
              </span>
            )}
          </span>
        )}
        {isMe && <span className="text-[9px] font-mono text-lime-deep flex-shrink-0">●</span>}
      </span>
    );
  };

  return (
    <div className={`px-3 py-2 rounded ${rowBg}`}>
      <div
        className="grid items-center gap-2"
        style={{ gridTemplateColumns: '70px 1fr auto 1fr 80px' }}
      >
        {/* Date */}
        <div className="min-w-0">
          <div className="font-mono text-[10px] text-ink-3 uppercase tracking-wider whitespace-nowrap">
            {dateLabel}
          </div>
          <div className="font-mono text-[10px] text-ink-3 whitespace-nowrap">{timeLabel}</div>
        </div>

        {/* Home team */}
        <div
          className="min-w-0 px-2 py-1 rounded relative"
          style={{ background: inT1 ? 'rgba(96, 165, 250, 0.12)' : 'transparent' }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-0.5 min-w-0">
              <PlayerName playerIdRef={m.team1.p1Id} isWinner={t1Won} isT1Side={true} />
              <PlayerName playerIdRef={m.team1.p2Id} isWinner={t1Won} isT1Side={true} />
            </div>
            {inT1 && teamBaseChange != null && (
              <div
                className={`font-mono text-[11px] font-bold flex-shrink-0 ${
                  teamBaseChange > 0
                    ? 'text-lime-deep'
                    : teamBaseChange < 0
                      ? 'text-rose'
                      : 'text-ink-3'
                }`}
                title={t('players.matchRow.baseChangeTitle')}
              >
                {teamBaseChange > 0 ? '+' : ''}
                {teamBaseChange}
              </div>
            )}
          </div>
        </div>

        {/* Score (set wins) */}
        <div className="flex items-center justify-center gap-1.5 px-2">
          <span
            className={`font-mono text-base font-bold ${t1Won ? 'text-lime-deep' : 'text-ink-3'}`}
          >
            {t1SetWins}
          </span>
          <span className="font-mono text-[10px] text-ink-4">–</span>
          <span
            className={`font-mono text-base font-bold ${t2Won ? 'text-lime-deep' : 'text-ink-3'}`}
          >
            {t2SetWins}
          </span>
        </div>

        {/* Away team */}
        <div
          className="min-w-0 px-2 py-1 rounded relative"
          style={{ background: !inT1 ? 'rgba(96, 165, 250, 0.12)' : 'transparent' }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-0.5 min-w-0">
              <PlayerName playerIdRef={m.team2.p1Id} isWinner={t2Won} isT1Side={false} />
              <PlayerName playerIdRef={m.team2.p2Id} isWinner={t2Won} isT1Side={false} />
            </div>
            {!inT1 && teamBaseChange != null && (
              <div
                className={`font-mono text-[11px] font-bold flex-shrink-0 ${
                  teamBaseChange > 0
                    ? 'text-lime-deep'
                    : teamBaseChange < 0
                      ? 'text-rose'
                      : 'text-ink-3'
                }`}
                title={t('players.matchRow.baseChangeTitle')}
              >
                {teamBaseChange > 0 ? '+' : ''}
                {teamBaseChange}
              </div>
            )}
          </div>
        </div>

        {/* Result */}
        <div className="text-right">
          {playerWon ? (
            <span className="inline-block px-2 py-0.5 rounded font-mono text-[10px] font-semibold uppercase tracking-wider bg-lime-bg text-lime-deep">
              {t('players.common.win')}
            </span>
          ) : (
            <span className="inline-block px-2 py-0.5 rounded font-mono text-[10px] font-semibold uppercase tracking-wider bg-rose-soft text-rose">
              {t('players.common.loss')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
